import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { endpoints } from "@/lib/api";
import { Check, Plus, ShoppingBasket, Trash2, Send } from "lucide-react";
import { toast } from "sonner";
import SendGroceryDialog from "@/components/SendGroceryDialog";

export default function GroceryPanel() {
  const qc = useQueryClient();
  const { data: items = [] } = useQuery({
    queryKey: ["grocery"],
    queryFn: endpoints.grocery,
  });

  const [text, setText] = useState("");
  const [sendOpen, setSendOpen] = useState(false);

  const createMut = useMutation({
    mutationFn: endpoints.createGrocery,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["grocery"] });
      setText("");
    },
  });
  const updateMut = useMutation({
    mutationFn: ({ id, payload }) => endpoints.updateGrocery(id, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["grocery"] }),
  });
  const delMut = useMutation({
    mutationFn: endpoints.deleteGrocery,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["grocery"] }),
  });
  const clearMut = useMutation({
    mutationFn: endpoints.clearCheckedGrocery,
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["grocery"] });
      toast.success(`Cleared ${res.deleted} items`);
    },
  });

  function add(e) {
    e?.preventDefault();
    if (!text.trim()) return;
    createMut.mutate({ name: text.trim() });
  }

  const checkedCount = items.filter((i) => i.checked).length;

  return (
    <div
      data-testid="grocery-card"
      className="wood-panel rounded-[2rem] border p-5 lg:p-6 flex flex-col overflow-hidden flex-1 min-h-0"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <ShoppingBasket size={20} className="text-[#D07A65]" />
          <h2 className="font-serif text-xl md:text-2xl tracking-tight text-[#EAE4DB]">
            Grocery
          </h2>
        </div>
        <div className="flex items-center gap-2">
          {checkedCount > 0 && (
            <button
              data-testid="clear-checked-btn"
              onClick={() => clearMut.mutate()}
              className="text-xs font-mono uppercase tracking-[0.15em] text-[#A6A098] hover:text-[#C85A5A] transition"
            >
              Clear ({checkedCount})
            </button>
          )}
          <button
            data-testid="open-send-btn"
            onClick={() => setSendOpen(true)}
            disabled={items.length === 0}
            className="h-9 w-9 rounded-full bg-[#2D2A28] border border-[#33302C] text-[#D07A65] hover:bg-[#33302C] flex items-center justify-center transition disabled:opacity-40 disabled:cursor-not-allowed"
            title="Send grocery list"
            aria-label="Send grocery list"
          >
            <Send size={15} />
          </button>
        </div>
      </div>
      <SendGroceryDialog open={sendOpen} onOpenChange={setSendOpen} />

      <form onSubmit={add} className="flex gap-2 mb-4">
        <input
          data-testid="grocery-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Add item…"
          className="flex-1 h-11 px-4 rounded-full bg-[#171514] border border-[#33302C] text-[#EAE4DB] placeholder:text-[#5a544e] focus:outline-none focus:border-[#D07A65] text-sm"
        />
        <button
          type="submit"
          data-testid="add-grocery-btn"
          className="h-11 w-11 rounded-full bg-[#D07A65] text-[#171514] flex items-center justify-center hover:opacity-90 transition shrink-0"
          aria-label="Add"
        >
          <Plus size={18} />
        </button>
      </form>

      <ul
        data-testid="grocery-list"
        className="space-y-1.5 flex-1 min-h-0 overflow-y-auto no-scrollbar pr-1"
      >
        {items.length === 0 && (
          <li className="text-[#A6A098] text-sm">List is empty.</li>
        )}
        {items.map((g) => (
          <li
            key={g.id}
            data-testid={`grocery-row-${g.id}`}
            className="group flex items-center gap-2.5 py-1.5"
          >
            <button
              data-testid={`toggle-grocery-${g.id}`}
              onClick={() =>
                updateMut.mutate({ id: g.id, payload: { checked: !g.checked } })
              }
              className="w-9 h-9 rounded-full border-2 flex items-center justify-center transition shrink-0"
              style={{
                borderColor: g.checked ? "#D07A65" : "#33302C",
                backgroundColor: g.checked ? "#D07A65" : "transparent",
              }}
              aria-label="Toggle"
            >
              {g.checked && <Check size={16} className="text-[#171514]" strokeWidth={3} />}
            </button>
            <div className="flex-1 min-w-0">
              <span
                className={`text-base ${
                  g.checked ? "line-through text-[#A6A098]" : "text-[#EAE4DB]"
                }`}
              >
                {g.name}
              </span>
              {g.quantity && (
                <span className="ml-2 text-xs font-mono text-[#A6A098] uppercase tracking-wider">
                  {g.quantity}
                </span>
              )}
            </div>
            <button
              data-testid={`delete-grocery-${g.id}`}
              onClick={() => delMut.mutate(g.id)}
              className="h-8 w-8 rounded-full text-[#A6A098]/60 hover:text-[#C85A5A] hover:bg-[#2D2A28] flex items-center justify-center transition shrink-0"
              aria-label="Delete"
            >
              <Trash2 size={14} />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
